import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CorrelationResult } from '@/lib/types';
import {
  ArrowsLeftRight,
  Minus,
  TrendDown,
  TrendUp,
} from '@phosphor-icons/react';

interface CorrelationDisplayProps {
  correlations: CorrelationResult[];
}

export function CorrelationDisplay({ correlations }: CorrelationDisplayProps) {
  const getCorrelationIcon = (correlation: number) => {
    if (correlation > 0.1) {
      return <TrendUp className='w-4 h-4 text-quality-good' />;
    }
    if (correlation < -0.1) {
      return <TrendDown className='w-4 h-4 text-quality-poor' />;
    }

    return <Minus className='w-4 h-4 text-muted-foreground' />;
  };

  const getCorrelationColor = (correlation: number) => {
    const abs = Math.abs(correlation);

    if (abs > 0.7) {
      return 'text-quality-good';
    }
    if (abs > 0.4) {
      return 'text-quality-warning';
    }

    return 'text-muted-foreground';
  };

  const getStrengthBadge = (type: string) => {
    const colors = {
      strong: 'bg-quality-good text-white',
      moderate: 'bg-quality-warning text-white',
      weak: 'bg-muted text-muted-foreground',
    };

    return colors[type as keyof typeof colors] || colors.weak;
  };

  if (correlations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <ArrowsLeftRight className='w-5 h-5' />
            Correlation Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-8 text-muted-foreground'>
            <ArrowsLeftRight className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <p>No significant correlations found between selected datasets.</p>
            <p className='text-sm mt-2'>
              Try selecting datasets with numeric fields for correlation
              analysis.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <ArrowsLeftRight className='w-5 h-5' />
          Correlation Analysis
          <Badge variant='secondary' className='ml-auto'>
            {correlations.length} correlations found
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-4'>
          {correlations.map((corr, index) => (
            <div
              key={index}
              className='border rounded-lg p-4 space-y-3 hover:shadow-sm transition-shadow'
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3 flex-1'>
                  <div className='flex-shrink-0'>
                    {getCorrelationIcon(corr.correlation)}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='font-medium text-sm leading-tight'>
                      {corr.dataset1} → {corr.dataset2}
                    </div>
                    <div className='flex items-center gap-2 mt-1'>
                      <Badge
                        className={`${getStrengthBadge(corr.type)} text-xs`}
                      >
                        {corr.type}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div
                  className={`text-lg font-bold ${getCorrelationColor(corr.correlation)} flex-shrink-0`}
                >
                  {corr.correlation > 0 ? '+' : ''}
                  {corr.correlation.toFixed(3)}
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
                <div className='flex items-center justify-between'>
                  <span className='text-muted-foreground'>Field 1:</span>
                  <span className='font-mono text-xs'>{corr.field1}</span>
                </div>
                <div className='flex items-center justify-between'>
                  <span className='text-muted-foreground'>Field 2:</span>
                  <span className='font-mono text-xs'>{corr.field2}</span>
                </div>
              </div>

              <div className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='text-muted-foreground'>Confidence:</span>
                  <span className='font-medium'>
                    {corr.confidence.toFixed(1)}%
                  </span>
                </div>
                <Progress value={corr.confidence} className='h-2' />
              </div>

              <div className='flex items-center justify-between text-sm'>
                <span className='text-muted-foreground'>Suggested Join:</span>
                <Badge variant='outline' className='text-xs uppercase'>
                  {corr.joinStrategy} join
                </Badge>
              </div>

              <div className='text-xs text-muted-foreground p-3 bg-muted/50 rounded border-l-2 border-muted'>
                <strong>Interpretation:</strong>{' '}
                {Math.abs(corr.correlation) > 0.7
                  ? 'Strong correlation suggests these variables are highly related.'
                  : Math.abs(corr.correlation) > 0.4
                    ? 'Moderate correlation indicates some relationship between variables.'
                    : 'Weak correlation suggests limited relationship between variables.'}
              </div>
            </div>
          ))}
        </div>

        <div className='mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <h4 className='font-semibold text-blue-900 mb-2'>
            Understanding Correlations
          </h4>
          <div className='text-sm text-blue-800 space-y-1'>
            <p>
              <strong>+1.0:</strong> Perfect positive correlation
            </p>
            <p>
              <strong>0.0:</strong> No correlation
            </p>
            <p>
              <strong>-1.0:</strong> Perfect negative correlation
            </p>
            <p>
              <strong>|0.7+|:</strong> Strong relationship
            </p>
            <p>
              <strong>|0.3-0.7|:</strong> Moderate relationship
            </p>
            <p>
              <strong>|&lt;0.3|:</strong> Weak relationship
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
